// src/pages/LoginPage.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  GraduationCap,
  Sparkles,
  ArrowRight,
  Lock,
  Mail,
  Eye,
  EyeOff,
  AlertCircle,
  ArrowLeft,
  Shield,
  Clock,
} from "lucide-react";
import { useAdminAuth } from "../admin/context/AdminAuthContext";
import logoImg from "../assets/navbar-logo.png";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAdminAuth();

  // Mode: "gateway" | "admin-login"
  const [activeView, setActiveView] = useState("gateway");

  // Admin Login Form State
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState("");

  // If already authenticated as admin, redirect to admin dashboard
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/admin/dashboard", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Handle Admin Login submission
  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoginError("");

    if (!email.trim() || !password.trim()) {
      setLoginError("Please enter both email and password.");
      return;
    }

    setLoginLoading(true);
    const result = await login(email, password, rememberMe);
    setLoginLoading(false);

    if (result.success) {
      navigate("/admin/dashboard", { replace: true });
    } else {
      setLoginError(result.error || "Invalid email or password. Please verify credentials.");
    }
  };

  return (
    <div className="min-h-[85vh] bg-gradient-to-b from-slate-100 via-slate-50 to-white flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-4xl">
        {/* Brand Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center p-3 bg-white rounded-2xl shadow-sm border border-slate-200/80 mb-3">
            <img src={logoImg} alt="Selva's IAS Academy" className="h-10 w-auto object-contain" />
          </div>
          <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight">
            Selva's <span className="text-red-800">IAS Academy</span>
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-md mx-auto">
            Select a portal to continue
          </p>
        </div>

        {/* ================= VIEW 1: PORTAL SELECTION (GATEWAY) ================= */}
        {activeView === "gateway" && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Option 1: LMS Portal (Coming Soon - Preserved) */}
              <div className="bg-white rounded-2xl p-7 border-2 border-slate-200 shadow-sm flex flex-col justify-between group opacity-90 hover:opacity-100 transition-all">
                <div>
                  <div className="w-12 h-12 rounded-xl bg-slate-100 text-slate-600 flex items-center justify-center mb-5">
                    <GraduationCap size={26} />
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600 bg-slate-100 px-2.5 py-0.5 rounded-full border border-slate-200">
                      Option 1
                    </span>
                    <span className="text-[10px] font-bold text-amber-800 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full flex items-center gap-1">
                      <Clock size={11} /> Coming Soon
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">LMS Portal</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Online classroom courses, video lecture archives, and batch schedules for enrolled academy students.
                  </p>
                </div>

                <div className="mt-6 pt-6 border-t border-slate-100">
                  <button
                    disabled
                    className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-slate-100 text-slate-400 font-semibold text-sm cursor-not-allowed border border-slate-200"
                  >
                    <span>LMS Portal (Coming Soon)</span>
                  </button>
                  <p className="text-[11px] text-center text-slate-400 mt-2">
                    LMS authentication is currently under development
                  </p>
                </div>
              </div>

              {/* Option 2: Current Affairs Portal (Active Admin Portal) */}
              <div 
                onClick={() => navigate("/admin/login")}
                className="bg-white rounded-2xl p-7 border-2 border-red-800 shadow-md hover:shadow-xl transition-all duration-300 flex flex-col justify-between group relative overflow-hidden ring-2 ring-red-900/10 cursor-pointer"
              >
                <div className="absolute top-0 right-0 bg-red-800 text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-bl-xl shadow-xs">
                  Active Portal
                </div>

                <div>
                  <div className="w-12 h-12 rounded-xl bg-red-50 text-red-800 flex items-center justify-center mb-5 group-hover:bg-red-800 group-hover:text-white transition-colors">
                    <Sparkles size={26} />
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-red-800 bg-red-50 px-2.5 py-0.5 rounded-full border border-red-200">
                      Option 2
                    </span>
                    <span className="text-xs font-semibold text-emerald-700 font-bold bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                      Administration
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Current Affairs Portal</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Management portal for Daily Current Affairs, Monthly Compendiums, Question Bank folders, Prelims tests, and Mains evaluations.
                  </p>
                </div>

                <div className="mt-6 pt-6 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate("/admin/login");
                    }}
                    className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-red-800 hover:bg-red-900 text-white font-semibold text-sm transition-all duration-200 shadow-xs hover:shadow-md hover:shadow-red-900/20"
                  >
                    <span>Login to Current Affairs Admin</span>
                    <ArrowRight size={16} />
                  </button>
                  <p className="text-[11px] text-center text-slate-500 mt-2">
                    Authorized faculty & administrative access
                  </p>
                </div>
              </div>
            </div>

            <div className="text-center pt-2">
              <span className="inline-flex items-center gap-1.5 text-xs text-slate-400">
                <Shield size={13} />
                <span>Selva's IAS Academy • Official Administration Portal</span>
              </span>
            </div>
          </div>
        )}

        {/* ================= VIEW 2: DEDICATED ADMIN LOGIN ================= */}
        {activeView === "admin-login" && (
          <div className="max-w-md mx-auto bg-white rounded-3xl shadow-xl border border-slate-200 p-8 sm:p-10">
            <button
              onClick={() => setActiveView("gateway")}
              className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-900 font-medium mb-6 transition-colors"
            >
              <ArrowLeft size={14} /> Back to Portal Options
            </button>

            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-2xl bg-red-50 text-red-800 flex items-center justify-center flex-shrink-0 border border-red-100">
                <Shield size={24} />
              </div>
              <div>
                <h2 className="text-xl font-black text-slate-900 tracking-tight">Admin Login</h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Selva's IAS Academy – Current Affairs Administration
                </p>
              </div>
            </div>

            {/* Error Message Alert */}
            {loginError && (
              <div className="mb-5 p-3.5 rounded-xl bg-red-50 border border-red-200 text-xs text-red-700 flex items-start gap-2.5">
                <AlertCircle size={16} className="flex-shrink-0 mt-0.5 text-red-600" />
                <div className="font-medium leading-relaxed">{loginError}</div>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleAdminLogin} className="space-y-4">
              {/* Email */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wider">
                  Admin Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-3.5 text-slate-400" size={17} />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@gmail.com"
                    className="w-full pl-10 pr-4 py-3 text-sm bg-slate-50 hover:bg-slate-100/60 focus:bg-white rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-red-900/20 focus:border-red-900 transition-all"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wider">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-3.5 text-slate-400" size={17} />
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full pl-10 pr-11 py-3 text-sm bg-slate-50 hover:bg-slate-100/60 focus:bg-white rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-red-900/20 focus:border-red-900 transition-all font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-3.5 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
              </div>

              {/* Remember Me */}
              <div className="flex items-center justify-between pt-1">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="w-4 h-4 rounded border-slate-300 text-red-900 focus:ring-red-900 cursor-pointer"
                  />
                  <span className="text-xs text-slate-600 font-medium">Remember this device</span>
                </label>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loginLoading}
                className="w-full mt-2 py-3 px-4 bg-gradient-to-r from-red-950 via-red-900 to-red-800 hover:from-red-900 hover:to-red-700 text-white font-bold text-sm rounded-xl transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loginLoading ? (
                  <span>Authenticating...</span>
                ) : (
                  <>
                    <span>Login to Admin Dashboard</span>
                    <ArrowRight size={16} />
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 pt-4 border-t border-slate-100 text-center">
              <p className="text-[11px] text-slate-400">
                Official Administrative Access • Selva's IAS Academy
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
